<template>
	<div
		ref="mapWrapper"
		class="relative w-full h-full"
	>
		<div
			:id="mapId"
			:class="[
				'absolute top-0 bottom-0 w-full h-full',
				mapClasses
			]"
		/>
		<div class="absolute text-xs text-mx-orange bottom-8 left-2">
			{{ modifiedText }}
		</div>
	</div>
</template>

<script setup lang="ts">
import type {
	ClusterConfig, LayerFilter, Legend, MapboxLayer, MapboxSource, PopupConfig
} from '@/components/mapbox/types'
import type { MapboxFeatureConfig } from '@/composables/mapbox/types'
import { DEFAULT_FEATURE_CONFIG } from '@/composables/mapbox/types'
import { useMapboxControls } from '@/composables/mapbox/useMapboxControls'
import { useMapboxDrawing } from '@/composables/mapbox/useMapboxDrawing'
import { useMapboxFeatures } from '@/composables/mapbox/useMapboxFeatures'
import { useMapboxLayers } from '@/composables/mapbox/useMapboxLayers'
import { useMapboxState } from '@/composables/mapbox/useMapboxState'
import { useAuth } from '@/composables/useAuth'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useToast } from '@/composables/useToast'
import {
	MAPBOX_API_KEY,
	MAPBOX_STYLE_DARK_URL,
	MAPBOX_STYLE_LIGHT_URL,
	MAPBOX_STYLE_SATELLITE_URL
} from '@/constants/index.js'
import { useColorMode, useResizeObserver } from '@vueuse/core'
import type {
	Feature, GeoJsonProperties, Point
} from 'geojson'
import type {
	CircleLayerSpecification,
	FillLayerSpecification,
	LineLayerSpecification,
	LngLatLike,
	SymbolLayerSpecification
} from 'mapbox-gl'
import mapboxgl from 'mapbox-gl'

const { addToast } = useToast()
const { bugsnagReport } = useErrorReporter()
const { hasAccountsExportPermission } = useAuth()

const props = defineProps({
	mapId: {
		type: String,
		required: true
	},
	mapClasses: {
		type: String,
		default: ''
	},
	sources: {
		type: Array as PropType<MapboxSource[]>,
		required: true
	},
	layers: {
		type: Array as PropType<MapboxLayer[]>,
		required: true
	},
	legend: {
		type: Array as PropType<Legend[]>,
		default: () => []
	},
	icons: {
		type: Array as PropType<{ id: string
			url: string
			sdf?: boolean }[]>,
		default: () => []
	},
	defaultCenter: {
		type: Array as unknown as PropType<LngLatLike>,
		default: () => [ 0, 0 ]
	},
	defaultZoom: {
		type: Number,
		default: 10
	},
	pitch: {
		type: Number,
		default: 0
	},
	clusterConfig: {
		type: Object as PropType<ClusterConfig>,
		default: () => ({})
	},
	drawControl: {
		type: Boolean,
		default: false
	},
	styleSwitcherControl: {
		type: Boolean,
		default: false
	},
	fullscreenControl: {
		type: Boolean,
		default: false
	},
	navigationControl: {
		type: Boolean,
		default: false
	},
	layerVisibilityControl: {
		type: Boolean,
		default: false
	},
	legendControl: {
		type: Boolean,
		default: false
	},
	activePolygonControl: {
		type: Boolean,
		default: false
	},
	activePolygonLabel: {
		type: String,
		default: ''
	},
	featureConfig: {
		type: Object as PropType<MapboxFeatureConfig>,
		default: () => DEFAULT_FEATURE_CONFIG
	},
	defaultStyle: {
		type: String as PropType<'satellite' | 'dark' | 'light' | ''>,
		default: '',
		validator: (value: string) => [ '', 'satellite', 'dark', 'light' ].includes(value)
	},
	popup: { // optional, can also be set from state
		type: Object as PropType<PopupConfig | null>,
		default: null
	},
	layerFilters: {
		type: Array as PropType<LayerFilter[]>,
		default: () => []
	},
	modifiedText: {
		type: String,
		default: ''
	}
})

const {
	mapId,
	mapClasses,
	sources,
	layers,
	legend,
	icons,
	defaultCenter,
	defaultZoom,
	pitch,
	clusterConfig,
	drawControl,
	styleSwitcherControl,
	fullscreenControl,
	navigationControl,
	layerVisibilityControl,
	activePolygonControl,
	legendControl,
	activePolygonLabel,
	featureConfig,
	defaultStyle,
	popup,
	layerFilters,
	modifiedText
} = toRefs(props)

// Set up Mapbox state management
const {
	mapState,
	registerMap,
	setMapLoaded,
	setStyleLoaded,
	// setPopupState,
	updateLayerFilters
} = useMapboxState(mapId.value)

// Map instance and refs
const mapRef = ref<mapboxgl.Map>()
const mapWrapper = ref<HTMLDivElement | null>(null)
const styleId = ref('')
const sourcesApplied = ref(false)
const activePopup = ref<mapboxgl.Popup | null>(null)

// Set up layer management
const {
	layerVisibility, initLayerVisibility, addLayers
} = useMapboxLayers(mapId.value, layers.value, featureConfig.value)

// Watch for source changes and update the map
watch(sources, async () => {
	if (mapRef.value && mapState.value.isStyleLoaded) {
		sourcesApplied.value = false
		showLoadingOverlay()
		await updateMapFeatures()
		// Re-add layers to ensure they use the new sources
		addLayers()
		scheduleMarkerCheck()
	}
}, { deep: true })

// Set up custom controls
const {
	registerControlPosition,
	initStyleSwitcherControl,
	initLayerVisibilityControl,
	initActivePolygonControl,
	initLegendControl,
	initLassoActionControl,
	initLoadingControl,
	initFilterConflictControl,
	showLoadingOverlay,
	hideLoadingOverlay
} = useMapboxControls(mapId.value, featureConfig.value)

// Set up drawing functionality
const { initDraw } = useMapboxDrawing(mapId.value, featureConfig.value)

// Set up feature management
const {
	loadIcons,
	addImageFromCache,
	updateGeoJsonData,
	setupMapEventHandlers
} = useMapboxFeatures(mapId.value)

// Watch for changes to popup from state or props
watch([ popup, () => mapState.value.popup ], ([ propPopup, statePopup ]) => {
	if (propPopup && mapRef.value) {
		setActivePopup(propPopup)
	} else if (statePopup && mapRef.value) {
		setActivePopup(statePopup)
	}
}, { deep: true })

const setActivePopup = async (popup: PopupConfig) => {
	const offset = popup.offset ?? {
		left: [ 0, 0 ],
		right: [ 0, 0 ],
		top: [ 0, 0 ],
		bottom: [ 0, 0 ]
	}
	if (mapRef.value) {
		// remove any existing popup
		if (activePopup.value) {
			activePopup.value.remove()
		}
		const { coordinates, html } = popup
		activePopup.value = new mapboxgl.Popup({
			offset,
			className: 'popup',
			closeButton: false
		})
			.setLngLat(coordinates)
			.setHTML(html)
			.addTo(mapRef.value)

		// Zoom to popup if an active marker and active source is set
		const shouldZoom = mapState.value.activeMarkerId && mapState.value.activeSourceId
		if (shouldZoom && mapRef.value) {
			const map = mapState.value.mapInstance
			if (!map) {
				return
			}

			try {
				// First try to find marker without zooming out
				const findMarkerWithoutZoom = map.querySourceFeatures(mapState.value.activeSourceId)
					.find((feature: Feature) => feature.properties?.[featureConfig.value.idProperty] === mapState.value.activeMarkerId) as Feature<Point, GeoJsonProperties>

				const customEasingFunction = (t: number) => {
					// Custom easing function for smoother animation
					return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
				}

				if (findMarkerWithoutZoom) {
					// If marker is already in view, just zoom to it directly
					const coordinates = findMarkerWithoutZoom.geometry.coordinates as [number, number]
					mapRef.value.flyTo({
						center: coordinates,
						zoom: 15,
						speed: 1.5, // Faster animation
						essential: true,
						easing: customEasingFunction
					})
					return
				}

				// Use a throttled check to improve performance
				let lastCheckTime = 0
				let markerFound = false

				// More efficient function to check for marker with throttling
				const checkForMarker = () => {
					const now = performance.now()
					// Only check every 100ms to reduce load
					if (now - lastCheckTime < 100) {
						return
					}
					lastCheckTime = now

					const features = map.querySourceFeatures(mapState.value.activeSourceId)
					const feature = features.find(f =>
						f.properties?.[featureConfig.value.idProperty] === mapState.value.activeMarkerId
					) as Feature<Point, GeoJsonProperties>

					if (feature && !markerFound) {
						markerFound = true
						mapRef.value?.off('move', checkForMarker)

						const coordinates = feature.geometry.coordinates as [number, number]

						// Use a more gentle animation to reduce jitter
						mapRef.value?.flyTo({
							center: coordinates,
							zoom: 15,
							speed: 1.8,
							essential: true,
							curve: 1, // Linear curve for smoother transition
							easing: customEasingFunction
						})
					}
				}

				// Start with a smoother zoom out
				const initialZoom = mapRef.value.getZoom()
				const targetZoom = Math.max(initialZoom - 4, 3)

				// Optimize rendering during animation
				map.transform.cameraToCenterDistance = 1 // Reduce perspective calculation cost

				mapRef.value.on('move', checkForMarker)

				// Use optimized animation parameters
				mapRef.value.flyTo({
					zoom: targetZoom,
					speed: 1.5, // Slower for smoother animation
					curve: 1.8, // More curved for smoother feel
					essential: true,
					easing: t => t // Linear easing for smoother camera movement
				})

				// Handle the final state
				await new Promise<void>(resolve => {
					const onMoveEnd = () => {
						mapRef.value?.off('move', checkForMarker)

						// Reset camera optimization
						//
						// This property affects how the map calculates perspective and camera transformations.
						// Setting it to 1 reduces the computational cost of perspective calculations during
						// complex animations like flying to locations or zooming, which helps maintain smooth performance.
						//
						// After the animation completes, this cleanup code checks if the optimization
						// is still active (value = 1) and resets it back to 0, which returns the map
						// to its standard rendering mode.
						if (map.transform.cameraToCenterDistance === 1) {
							map.transform.cameraToCenterDistance = 0
						}

						if (!markerFound) {
							// Final check at the lowest zoom level
							const features = map.querySourceFeatures(mapState.value.activeSourceId)
							const feature = features.find(f =>
								f.properties?.[featureConfig.value.idProperty] === mapState.value.activeMarkerId
							) as Feature<Point, GeoJsonProperties>

							if (feature) {
								const coordinates = feature.geometry.coordinates as [number, number]
								mapRef.value?.flyTo({
									center: coordinates,
									zoom: 15,
									speed: 1.8,
									essential: true,
									easing: customEasingFunction
								})
							} else {
								addToast({
									notificationType: 'error',
									title: 'Marker not found',
									message: 'Close popup and zoom out further and try again.'
								})
							}
						}
						resolve()
					}

					mapRef.value?.once('moveend', onMoveEnd)
				})
			} catch (error) {
				bugsnagReport({
					error: error instanceof Error ? error : new Error('Failed to zoom to marker'),
					showToast: true,
					toast: {
						notificationType: 'error',
						title: 'Failed to zoom to marker.',
						message: `Marker ID ${mapState.value.activeMarkerId} not found on map.`
					}
				})
			}
		}
	}
}

// Watch for changes to layerFilters and update the map
watch(layerFilters, newFilters => {
	if (newFilters) {
		updateLayerFilters(newFilters)
	}
}, { deep: true })

// Initialize map style based on user preferences
const getInitialStyle = () => {
	if (defaultStyle.value === '') {
		return colorMode.value === 'dark' ? MAPBOX_STYLE_DARK_URL : MAPBOX_STYLE_LIGHT_URL
	} else {
		switch (defaultStyle.value) {
			case 'satellite':
				return MAPBOX_STYLE_SATELLITE_URL
			case 'dark':
				return MAPBOX_STYLE_DARK_URL
			case 'light':
				return MAPBOX_STYLE_LIGHT_URL
			default:
				return MAPBOX_STYLE_LIGHT_URL
		}
	}
}

// Set viewport position and zoom
const setViewport = () => {
	if (mapRef.value) {
		mapRef.value.setPitch(pitch?.value || 0)
		const center = defaultCenter?.value || [ 0, 0 ]
		mapRef.value.jumpTo({
			center,
			zoom: defaultZoom?.value
		})
	}
}

// Update map features (icons and data) — async so icons are loaded before layers are added
const updateMapFeatures = async () => {
	if (mapRef.value) {
		await loadIcons(mapRef.value, icons?.value || [])
		updateGeoJsonData(mapRef.value, sources.value)
		sourcesApplied.value = true
	}
}

const markerLayers = computed(() => {
	return layers.value.filter(layer => {
		const layerType = (layer.type ?? (layer.layer as Partial<FillLayerSpecification | LineLayerSpecification | SymbolLayerSpecification | CircleLayerSpecification>)?.type) as string | undefined
		return layerType === 'circle' || layerType === 'symbol'
	})
})

const markerLayerIds = computed(() => markerLayers.value.map(layer => layer.id))

const markerSourceIds = computed(() => markerLayers.value
	.map(layer => (layer.layer as Partial<FillLayerSpecification | LineLayerSpecification | SymbolLayerSpecification | CircleLayerSpecification>)?.source)
	.filter((sourceId): sourceId is string => typeof sourceId === 'string')
)

function hasFeatureCollectionData(data: any): boolean {
	if (!data) {
		return false
	}
	if (typeof data === 'string') {
		return data.length > 0
	}
	if (Array.isArray(data)) {
		return data.length > 0
	}
	if (typeof data === 'object' && data.type === 'FeatureCollection' && Array.isArray((data as any).features)) {
		return (data as any).features.length > 0
	}
	if (typeof data === 'object' && data.type === 'Feature') {
		return true
	}
	return false
}

function hasMarkerData(): boolean {
	const map = mapRef.value as (mapboxgl.Map & { __mbxSources?: Record<string, any> }) | undefined
	if (!map) {
		return false
	}

	if (!markerLayerIds.value.length) {
		return sourcesApplied.value
	}

	let missingLayer = false
	for (const layerId of markerLayerIds.value) {
		const layerExists = !!map.getLayer(layerId)
		if (!layerExists) {
			missingLayer = true
			continue
		}
		const renderedFeatures = map.queryRenderedFeatures({ layers: [ layerId ] })
		if (renderedFeatures.length > 0) {
			return true
		}
	}

	if (missingLayer) {
		return false
	}

	if (!markerSourceIds.value.length) {
		return sourcesApplied.value
	}

	const hasSourceFeatures = markerSourceIds.value.some(sourceId => {
		const sourceSpec = map.__mbxSources?.[sourceId]
		return hasFeatureCollectionData(sourceSpec?.data)
	})

	if (hasSourceFeatures) {
		return true
	}

	const initializedSources = markerSourceIds.value.every(sourceId => !!map.getSource(sourceId))

	return initializedSources && sourcesApplied.value
}

function handleIdleMarkerCheck() {
	if (hasMarkerData()) {
		hideLoadingOverlay()
		mapRef.value?.off('idle', handleIdleMarkerCheck)
	}
}

function scheduleMarkerCheck() {
	const map = mapRef.value
	if (!map) {
		return
	}
	map.off('idle', handleIdleMarkerCheck)
	map.on('idle', handleIdleMarkerCheck)
}

// Define layer definitions for visibility control
const layerDefinitions = computed(() => {
	return layers.value.map(layer => ({
		id: layer.id,
		name: layer.label ?? layer.id,
		visibility: layerVisibility.value[layer.id] ?? false,
		hideInVisibilityControl: layer.hideInVisibilityControl ?? false,
		siblingLayerIds: layer.siblingLayerIds ?? []
	}))
})

// Style switcher options
const styleSwitcherOptions = [
	{
		label: 'Satellite',
		url: MAPBOX_STYLE_SATELLITE_URL
	},
	{
		label: 'Streets Dark',
		url: MAPBOX_STYLE_DARK_URL
	},
	{
		label: 'Streets Light',
		url: MAPBOX_STYLE_LIGHT_URL
	}
]

// Initialize the map
const initializeMap = () => {
	mapboxgl.accessToken = MAPBOX_API_KEY

	mapRef.value = new mapboxgl.Map({
		container: mapId.value,
		style: getInitialStyle(),
		zoom: defaultZoom?.value,
		attributionControl: false,
		dragRotate: false,
		touchZoomRotate: true,
		touchPitch: false,
		pitchWithRotate: false,
		keyboard: false,
		center: defaultCenter?.value || [ 0, 0 ]
	})

	// Register map in state
	registerMap(mapRef.value)
	sourcesApplied.value = false
	initLoadingControl(mapRef.value)
	showLoadingOverlay()

	// Register custom control position
	registerControlPosition(mapRef.value, 'top-center')

	// Add event listeners
	mapRef.value.on('load', async () => {
		sourcesApplied.value = false
		showLoadingOverlay()
		await updateMapFeatures()
		addLayers()
		setViewport()
		mapRef.value?.touchZoomRotate.disableRotation()
		setMapLoaded()
		scheduleMarkerCheck()
	})

	mapRef.value.on('style.load', async () => {
		sourcesApplied.value = false
		showLoadingOverlay()
		styleId.value = mapRef.value?.getStyle()?.name || ''
		setStyleLoaded()
		// Only run full sequence when sources are available (style switches).
		// On initial load, sources arrive later via the sources watch.
		if (sources.value?.length) {
			await updateMapFeatures()
			addLayers()
			scheduleMarkerCheck()
		}
	})

	mapRef.value.on('styleimagemissing', e => {
		const icon = icons.value?.find(i => i.id === e.id)
		if (icon && mapRef.value) {
			// Re-add from in-memory cache synchronously to avoid the async gap
			// that causes WebGL draw calls with unregistered icon textures
			if (!addImageFromCache(mapRef.value, icon)) {
				mapRef.value.loadImage(icon.url, (error, image) => {
					if (error) {
						bugsnagReport({ error: error instanceof Error ? error : new Error(`${icon.id} Failed to Load`) })
						return
					}
					if (image && !mapRef.value?.hasImage(icon.id)) {
						mapRef.value?.addImage(icon.id, image, { sdf: !!icon.sdf })
					}
				})
			}
		}
	})

	mapRef.value.on('error', event => {
		const error = event.error ?? new Error('Mapbox failed to load')
		bugsnagReport({ error })
		hideLoadingOverlay()
	})

	// Set up map event handlers
	setupMapEventHandlers(mapRef.value, layers.value)

	// Add standard controls
	mapRef.value.addControl(new mapboxgl.AttributionControl({ customAttribution: 'Maverix Broadband' }))

	if (styleSwitcherControl.value) {
		initStyleSwitcherControl(mapRef.value, styleSwitcherOptions)
	}

	if (fullscreenControl.value) {
		mapRef.value.addControl(new mapboxgl.FullscreenControl(), 'top-right')
	}

	if (navigationControl.value) {
		mapRef.value.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
	}

	initFilterConflictControl(mapRef.value)

	if (activePolygonControl.value) {
		initActivePolygonControl(mapRef.value, activePolygonLabel.value || '')
	}

	if (legendControl.value && legend.value) {
		initLegendControl(mapRef.value, legend.value)
	}

	if (layerVisibilityControl.value) {
		initLayerVisibility()
		initLayerVisibilityControl(mapRef.value, layerDefinitions.value)
	}

	if (drawControl.value) {
		// Initialize draw control
		initDraw()

		// Initialize lasso action control
		initLassoActionControl(mapRef.value, hasAccountsExportPermission.value)
	}
}

// Handle color mode changes
const colorMode = useColorMode()
watch(colorMode, () => {
	styleId.value = mapRef.value?.getStyle()?.name || ''

	if (styleId.value?.toLowerCase()?.includes('satellite')) {
		return
	}

	const newStyle = styleSwitcherOptions.find(style =>
		style.label.toLowerCase().includes(colorMode.value.toLowerCase())
	)

	if (newStyle) {
		sourcesApplied.value = false
		showLoadingOverlay()
		mapRef.value?.setStyle(newStyle.url)
	}
})

// Handle resize events
useResizeObserver(mapWrapper, () => {
	mapRef.value?.resize()
})

// Initialize on mount
onMounted(() => {
	initializeMap()
})

onUnmounted(() => {
	mapRef.value?.off('idle', handleIdleMarkerCheck)
})
</script>

<style lang="postcss">
html, body, #__nuxt {
  height: 100%;
  margin: 0;
}

.mapboxgl-ctrl-top-center {
  top: 0;
  left: 50%;
  position: absolute;
  pointer-events: none;
  z-index: 2;
}

.mapboxgl-ctrl-top-center .mapboxgl-ctrl {
  margin: 10px 0px 0px 10px;
}

.mapboxgl-popup-content {
  @apply rounded-lg min-w-[400px];
  text-align: left !important;
  background: linear-gradient(45deg, #ffffff, #F9F9F9);
}

.dark .mapboxgl-popup-content {
  background: linear-gradient(45deg, #162327, #1a2a2e);
}

.mapboxgl-popup-anchor-top .mapboxgl-popup-tip,
.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
  border-bottom-color: #fff;
}
.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip,
.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
  border-top-color: #fff;
}
.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  border-right-color: #fff;
}
.mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  border-left-color: #fff;
}

.dark .mapboxgl-popup-anchor-top .mapboxgl-popup-tip,
.dark .mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
.dark .mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
  border-bottom-color: #18272B;
}

.dark .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip,
.dark .mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
.dark .mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
  border-top-color: #18272B;
}

.dark .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  border-right-color: #18272B;
}

.dark .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  border-left-color: #18272B;
}
</style>
